import React from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface PickProgressBarProps {
  answeredQuestions: boolean[]; // Array of boolean for each question (including tiebreaker)
  onSubmit: () => void;
  isSubmitting: boolean;
  className?: string;
}

/**
 * PickProgressBar - Bottom progress indicator showing answered questions
 * Shows circles for each question, submit button appears when all answered
 */
const PickProgressBar: React.FC<PickProgressBarProps> = ({
  answeredQuestions,
  onSubmit,
  isSubmitting,
  className = '',
}) => {
  const { t } = useTranslation();
  const totalQuestions = answeredQuestions.length;
  const answeredCount = answeredQuestions.filter(Boolean).length;
  const allAnswered = answeredCount === totalQuestions;

  return (
    <div
      className={
        'fixed bottom-0 left-0 right-0 z-40 bg-app-background border-t border-border/30 p-4 pb-safe ' +
        className
      }
      data-test-id="pick-progress-bar"
    >
      {/* Progress Circles */}
      <div className="flex items-center justify-center gap-0 mb-4">
        {answeredQuestions.map((isAnswered, index) => {
          const isLast = index === totalQuestions - 1;
          const circleClass = isAnswered ? 'bg-[#01A925]' : 'bg-[#4B5563]';

          // Check if both current and next circles are answered for green line
          const nextIsAnswered = !isLast && answeredQuestions[index + 1];
          const lineIsGreen = isAnswered && nextIsAnswered;
          const lineClass = lineIsGreen ? 'w-4 h-1 bg-[#01A925]' : 'w-4 h-0.5 bg-[#4B5563]';

          return (
            <React.Fragment key={index}>
              {/* Circle */}
              <div
                className={'flex items-center justify-center w-10 h-10 rounded-full ' + circleClass}
                data-test-id={'progress-circle-' + (index + 1)}
                data-test-answered={isAnswered}
              >
                {isAnswered ? (
                  <Check className="w-5 h-5 text-white" />
                ) : (
                  <span className="text-white text-base font-gilroy font-bold">{index + 1}</span>
                )}
              </div>

              {/* Line connector (not after last circle) */}
              {!isLast && <div className={lineClass} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Submit Button - Only show when all answered */}
      {allAnswered && (
        <Button
          onClick={onSubmit}
          disabled={isSubmitting}
          variant="bet99"
          className="w-full py-3 text-base font-gilroy"
          data-test-id="submit-picks-button"
        >
          {isSubmitting ? t('actions.submitting') : t('actions.submitPicks')}
        </Button>
      )}
    </div>
  );
};

export default PickProgressBar;
